import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Badge, Box } from "@material-ui/core";
import LikeDislikeComponent from 'components/Reaction/LikeDislikeComponent'; // Adjust path as needed


import { makeStyles } from '@material-ui/core/styles';

// Custom styles for the Badge component
const useStyles = makeStyles(theme => ({
  success: {
    '& .MuiBadge-badge': {
      backgroundColor: '#4caf50',
      color: '#fff',
      fontWeight: 'bold',
      boxShadow: '0 2px 4px rgba(76, 175, 80, 0.4)',
      animation: '$pulse 1.5s infinite',
      top: '-5% !important',
    }
  },
  warning: {
    '& .MuiBadge-badge': {
      backgroundColor: '#ff9800',
      color: '#fff',
      fontWeight: 'bold',
      boxShadow: '0 2px 4px rgba(255, 152, 0, 0.4)',
      top: '-5% !important',
    }
  },
  error: {
    '& .MuiBadge-badge': {
      backgroundColor: '#f44336',
      color: '#fff',
      fontWeight: 'bold',
      boxShadow: '0 2px 4px rgba(244, 67, 54, 0.4)',
      top: '-5% !important',
    }
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)'
    },
    '50%': {
      transform: 'scale(1.1)'
    },
    '100%': {
      transform: 'scale(1)'
    }
  },
  '@keyframes blink': {
    '0%, 50%': {
      opacity: 1
    },
    '51%, 100%': {
      opacity: 0
    }
  },
  blinkingCursor: {
    animation: '$blink 1s infinite',
    fontWeight: 'bold',
    color: '#1976d2'
  }
}));

const LeadScorePopup = ({ calculatedScore }) => {
  const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [aiContent, setAiContent] = useState("");
    const [displayedText, setDisplayedText] = useState("");
    const [isTyping, setIsTyping] = useState(false);

    // Function to generate AI content dynamically
    const generateLeadSummary = (calculatedScore) => {
        const { defaultScore, finalScore, scoringReasons } = calculatedScore;
    
        let response = `🤖  Smart Lead Scoring – Powered by AI\n`;
        response += `🔹 Final Score: ${finalScore} / ${defaultScore}\n\n`;
        response += `🔍 Score Breakdown:\n`;
    
        // Mapping category descriptions dynamically
        const categoryDescriptions = {
            disposition: "Disposition Impact",
            entryBase: "Initial Engagement",
            talktime: "Call Duration",
            custom: "Custom Factors",
            remarketingImpact: "Remarketing Effect",
            resetOnDisposition: "Score Adjustments",
        };
    
        // Dynamically generate content for each category
        Object.keys(scoringReasons).forEach((category) => {
            if (scoringReasons[category].length > 0) {
                scoringReasons[category].forEach(reason => {
                    let description = categoryDescriptions[category] || category;
                    let scoreImpact = reason.score > 0 ? `+${reason.score}` : `${reason.score}`;
                    
                    response += `✅ ${description}: ${scoreImpact} (${reason.reason})\n`;
    
                    // Adding facts if available
                    if (reason.facts) {
                        Object.entries(reason.facts).forEach(([key, value]) => {
                            try{
                            let factData = JSON.parse(value);
                            response += `   - ${key.replace(/([A-Z])/g, ' $1')}: ${factData.factResult}\n`;
                            }
                            catch(e){
                                console.log(e)
                            }
                        });
                    }
                });
            }
        });
    
        // Final Recommendation
        response += ``;
        if (finalScore < 50) {
            response += `- The lead score is below average. Consider a follow-up via email, WhatsApp, or a direct call.\n`;
        } else {
            response += `- This lead shows strong potential! Engage with a personalized offer or direct sales outreach.\n`;
        }
    
        response += `\nWould you like me to analyze another lead? 😊`;
    
        return response;
    };

    // Typing effect function
    const typeText = (text, speed = 30) => {
        setIsTyping(true);
        setDisplayedText("");
        let index = 0;
        
        const typeInterval = setInterval(() => {
            if (index < text.length) {
                setDisplayedText(text.substring(0, index + 1));
                index++;
            } else {
                clearInterval(typeInterval);
                setIsTyping(false);
            }
        }, speed);
        
        return typeInterval;
    };

    // Handle Button Click (Open Popup & Set AI Content)
    const handleClickOpen = () => {
        const content = generateLeadSummary(calculatedScore);
        setAiContent(content);
        setOpen(true);
        // Start typing effect
        typeText(content);
    };

    // Handle Close
    const handleClose = () => {
        setOpen(false);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        if (score >= 40) return 'error';
        return 'error';
    };

    return (
        <>
            {/* Button to Open Popup */}
            <Box style={{ textAlign: "center" }}>
                <Badge 
                badgeContent={calculatedScore && calculatedScore.finalScore} 
                className={classes[getScoreColor(calculatedScore && calculatedScore.finalScore)]}
                overlap="circular"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}>
                    <Button variant="contained" color="primary" onClick={handleClickOpen}>
                        <i className="fa fa-magic" aria-hidden="true" style={{ color: "#fff000" }}></i>{" "}
                        Score
                    </Button>
                </Badge>
            </Box>

            {/* Popup Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>🤖 AI Generated Lead Scoring Insights</DialogTitle>
                <DialogContent>
                    <Typography style={{ 
                        whiteSpace: "pre-line",
                        fontFamily: "monospace",
                        minHeight: "200px"
                    }}>
                        {displayedText}
                        {isTyping && <span className={classes.blinkingCursor}>|</span>}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <LikeDislikeComponent
                        itemId="LeadScore" // Assuming your item has an _id
                        itemType="LeadScoreReport" // Define a unique itemType
                    />
                    <Button onClick={handleClose} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LeadScorePopup;